// Marquee of upcoming PRCA entry closings. Global, admin-managed data.
// Behaviour: hover (desktop) or press-and-hold (touch) pauses the scroll; you can
// drag it backwards and it resumes on release. Tapping a rodeo opens its Info modal.
import { useRef, useEffect } from "react";
import { X } from "lucide-react";
import { daysUntilMountain } from "@/lib/ui";

function fmtDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function daysLeft(iso) {
  return daysUntilMountain(iso);
}

export function EntryClosingTicker({ items, onClick, onItem, onDismiss }) {
  const innerRef = useRef(null);
  const offsetRef = useRef(0);
  const halfRef = useRef(0);
  const pausedRef = useRef(false);   // desktop hover
  const draggingRef = useRef(false); // active drag / press-hold
  const lastTsRef = useRef(0);
  const dragRef = useRef({ startX: 0, startOffset: 0, moved: 0 });
  const suppressRef = useRef(false); // swallow the click that ends a drag

  const count = items?.length || 0;

  // Measure the width of ONE copy of the list (we render it twice for a seamless loop).
  useEffect(() => {
    const measure = () => { if (innerRef.current) halfRef.current = innerRef.current.scrollWidth / 2; };
    measure();
    let ro;
    if (window.ResizeObserver && innerRef.current) { ro = new ResizeObserver(measure); ro.observe(innerRef.current); }
    return () => ro && ro.disconnect();
  }, [count]);

  // rAF scroll loop — constant, slow, readable pace (px/sec) regardless of item count.
  useEffect(() => {
    if (!count) return;
    const SPEED = 22; // px per second — slow enough to read comfortably
    let raf = 0;
    const step = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000; lastTsRef.current = ts;
      const half = halfRef.current;
      if (half > 0 && !pausedRef.current && !draggingRef.current) {
        offsetRef.current -= SPEED * dt;
      }
      if (half > 0) {
        if (offsetRef.current <= -half) offsetRef.current += half;
        if (offsetRef.current > 0) offsetRef.current -= half;
      }
      if (innerRef.current) innerRef.current.style.transform = `translateX(${offsetRef.current}px)`;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [count]);

  const onPointerDown = (e) => {
    draggingRef.current = true;
    dragRef.current = { startX: e.clientX, startOffset: offsetRef.current, moved: 0 };
    const move = (ev) => {
      if (!draggingRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      dragRef.current.moved = Math.max(dragRef.current.moved, Math.abs(dx));
      offsetRef.current = dragRef.current.startOffset + dx; // drag right → rewind
    };
    const up = () => {
      draggingRef.current = false;
      lastTsRef.current = 0; // reset dt so it doesn't jump after a long hold
      if (dragRef.current.moved > 6) { suppressRef.current = true; setTimeout(() => { suppressRef.current = false; }, 60); }
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  if (!items || !items.length) return null;
  const loop = items.concat(items);
  return (
    <div data-testid="entry-ticker" className="h-7 overflow-hidden flex items-stretch bg-[#C8000F] text-white border-b border-black/25 select-none">
      <button type="button" onClick={onClick} data-testid="entry-ticker-label"
        className="shrink-0 px-2.5 flex items-center gap-1.5 bg-black/20 text-[10px] font-extrabold uppercase tracking-wider active:bg-black/30 hover:bg-black/30">
        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Entries Close
      </button>
      <div className="relative flex-1 overflow-hidden cursor-grab active:cursor-grabbing"
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; lastTsRef.current = 0; }}
        onPointerDown={onPointerDown}
        onClickCapture={(e) => { if (suppressRef.current) { e.preventDefault(); e.stopPropagation(); suppressRef.current = false; } }}>
        <div ref={innerRef} className="absolute inset-y-0 left-0 flex items-center whitespace-nowrap will-change-transform">
          {loop.map((c, i) => {
            const dl = daysLeft(c.close_iso);
            const urgent = dl != null && dl <= 2;
            return (
              <button key={i} type="button" onClick={() => onItem && c.rodeo_id && onItem(c)}
                className="inline-flex items-center gap-1.5 px-5 text-[12px] hover:underline">
                <span className="opacity-60">•</span>
                <b className="font-bold">{c.rodeo}</b>
                {c.state ? <span className="opacity-80">· {c.state}</span> : null}
                <span className="opacity-90">closes {c.close_iso ? fmtDate(c.close_iso) : c.close_raw}</span>
                {dl != null && (
                  <span className={`font-bold ${urgent ? "text-amber-200" : "opacity-95"}`}>
                    ({dl <= 0 ? "today" : dl === 1 ? "1 day" : `${dl} days`})
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} data-testid="entry-ticker-dismiss" title="Hide for now"
          className="shrink-0 px-2 flex items-center bg-black/20 text-white/90 active:bg-black/30 hover:bg-black/30">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
